import { teacherService } from "./TeacherService";

export function capturePage(route: string, path: string, routeTitle?: string): void {
  try {
    const pageInfo = {
      title: document.title,
      url: window.location.href,
      route: route || '',
      path: path || ''
    };

    const formattedContent = formatPageContent(pageInfo);

    teacherService.changePageContext({
      route: pageInfo.route,
      url: pageInfo.path,
      title: pageInfo.title || routeTitle,
      content: formattedContent
    });

  }
  catch (error) {
    teacherService.changePageContext({
      route: route || '',
      url: path || '',
      title: routeTitle || document.title,
      content: `Page: ${document.title || path}`
    });
  }
}

function formatPageContent(pageInfo: { title: string, url: string, route: string, path: string }): string {
  const processedElements = new Set<Element>();
  const contentItems: {type: string, text: string, level: number}[] = [];

  const mainContent = document.querySelector('main') || document.querySelector('.q-page-container') || document.body;

  const navElements = document.querySelectorAll('nav, .q-footer, .q-tabs, .q-bottom-tabs');
  navElements.forEach(nav => {
    if (isVisible(nav)) {
      addContentItem(contentItems, 'SECTION', 'NAVIGATION', 0);
      processElement(nav, 1, processedElements, contentItems);
    }
  });

  processElement(mainContent, 0, processedElements, contentItems);

  let formattedContent = `PAGE: ${pageInfo.title}\nURL: ${pageInfo.url}\nROUTE: ${pageInfo.route}\n\n`;
  formattedContent += "PAGE CONTENT:\n";

  let currentLevel = -1;

  for (const item of contentItems) {
    if (item.level < currentLevel) {
      formattedContent += "\n";
    }
    currentLevel = item.level;

    const indent = '  '.repeat(item.level);
    const prefix = item.type ? `[${item.type}] ` : '';

    formattedContent += `${indent}${prefix}${item.text}\n`;
  }

  const maxLength = 12000;
  if (formattedContent.length > maxLength) {
    formattedContent = formattedContent.substring(0, maxLength) + '... (truncated content)';
  }

  return formattedContent;
}

function processElement(el: Element, level = 0, processedElements: Set<Element>, contentItems: {type: string, text: string, level: number}[]) {
  if (!isVisible(el) || processedElements.has(el)) {
    return;
  }
  processedElements.add(el);

  const elementType = getElementType(el);
  let elementText = '';

  if (elementType) {
    if (elementType === 'IMAGE') {
      const alt = el.getAttribute('alt');
      const title = el.getAttribute('title');
      if (alt) {
        elementText = `Image: ${alt}`;
      }
      else if (title) {
        elementText = `Image: ${title}`;
      }
    }
    else if (elementType === 'FIELD') {
      const placeholder = el.getAttribute('placeholder');
      const label = el.getAttribute('aria-label');
      const name = el.getAttribute('name');
      const inputType = el.getAttribute('type') || el.tagName.toLowerCase();

      elementText = placeholder || label || name || '';
      if (elementText) {
        elementText += ` (${inputType})`;
      }
    }
    else {
      for (const node of Array.from(el.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          const nodeText = cleanText(node.textContent || '');
          if (nodeText) {
            elementText += nodeText + ' ';
          }
        }
      }
      elementText = cleanText(elementText);

      if (!elementText) {
        for (const child of Array.from(el.children)) {
          const childTag = child.tagName.toLowerCase();
          if (!['div', 'span', 'section', 'article'].includes(childTag) && isVisible(child)) {
            const childText = cleanText(child.textContent || '');
            if (childText) {
              elementText += childText + ' ';
            }
          }
        }
        elementText = cleanText(elementText);
      }

      if (!elementText && (elementType === 'CARD' || el.tagName.toLowerCase() === 'section')) {
        elementText = cleanText(el.textContent || '');
      }
    }

    if (elementText) {
      addContentItem(contentItems, elementType, elementText, level);
    }
  }
  else {
    const directText = Array.from(el.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => cleanText(node.textContent || ''))
      .filter(text => text.length > 10)
      .join(' ');

    if (directText) {
      if (directText.length > 50) {
        addContentItem(contentItems, 'PARAGRAPH', directText, level);
      }
      else {
        addContentItem(contentItems, 'TEXT', directText, level);
      }
    }
  }

  if (level < 15) {
    for (const child of Array.from(el.children)) {
      if (!processedElements.has(child)) {
        processElement(child, level + 1, processedElements, contentItems);
      }
    }
  }
}


function addContentItem(contentItems: {type: string, text: string, level: number}[], type: string, text: string, level: number) {
  const cleanedText = cleanText(text);
  if (cleanedText && cleanedText.length > 1) {
    if (!contentItems.some(item => item.text === cleanedText)) {
      contentItems.push({ type, text: cleanedText, level });
    }
  }
}


function cleanText(text: string): string {
  if (!text) {
    return '';
  }
  return text.replace(/\s+/g, ' ').trim();
}


function isVisible(el: Element): boolean {
  if (!el) {
    return false;
  }

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }

  const tagName = el.tagName.toLowerCase();
  if (['script', 'style', 'noscript', 'meta', 'link'].includes(tagName)) {
    return false;
  }

  return true;
}

function getElementType(el: Element): string {
  const tagName = el.tagName.toLowerCase();
  const classList = Array.from(el.classList).join(' ').toLowerCase();

  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
    return 'TITLE';
  }
  else if (tagName === 'button' || classList.includes('btn') || el.getAttribute('role') === 'button') {
    return 'BUTTON';
  }
  else if (tagName === 'a' || el.hasAttribute('href')) {
    return 'LINK';
  }
  else if (tagName === 'li' || classList.includes('item') || el.getAttribute('role') === 'listitem') {
    return 'ITEM';
  }
  else if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return 'FIELD';
  }
  else if (classList.includes('card') || classList.includes('panel')) {
    return 'CARD';
  }
  else if (tagName === 'p' || classList.includes('text')) {
    return 'TEXT';
  }
  else if (tagName === 'img' || tagName === 'figure') {
    return 'IMAGE';
  }
  else if (tagName === 'nav' || classList.includes('nav') || classList.includes('menu')) {
    return 'NAVIGATION';
  }
  else {
    return '';
  }
}
